import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, LoginResponse, SessionUser } from '@news-cms/shared';
import { ApiError, api } from './api';

/* ---------- API helpers ---------- */

async function meApi(): Promise<SessionUser | null> {
  try {
    const data = await api<{ user: SessionUser }>('/api/admin/auth/me');
    return data.user;
  } catch (e) {
    // Any failure to determine auth state (401, network error, CORS reject,
    // API down) is treated as "not authenticated" so /login route can still
    // render. Login mutation will surface the real error to the user via Alert.
    if (e instanceof ApiError && e.status !== 401) {
      console.warn('meApi error (treating as logged out):', e.code, e.message);
    } else if (!(e instanceof ApiError)) {
      console.warn('meApi network error (treating as logged out):', e);
    }
    return null;
  }
}

async function loginApi(input: LoginInput): Promise<LoginResponse> {
  return api<LoginResponse>('/api/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

async function verify2faApi(input: { challenge: string; code: string }): Promise<SessionUser> {
  const data = await api<{ user: SessionUser }>('/api/admin/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return data.user;
}

async function logoutApi(): Promise<void> {
  await api<{ ok: true }>('/api/admin/auth/logout', { method: 'POST' });
}

/* ---------- Query options ---------- */

export const sessionQueryOptions = queryOptions({
  queryKey: ['session'] as const,
  queryFn: meApi,
  staleTime: Infinity,
  retry: false,
});

/* ---------- Hooks ---------- */

export function useSession() {
  const q = useQuery(sessionQueryOptions);
  return {
    user: q.data ?? null,
    isLoading: q.isLoading,
    isAuthenticated: !!q.data,
  };
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: loginApi,
    onSuccess: (result) => {
      if ('user' in result) {
        qc.setQueryData(sessionQueryOptions.queryKey, result.user);
      }
      // 2FA branch: don't cache anything; the form switches to the code step
      // and a successful /verify will populate the session.
    },
  });
}

export function useVerify2faMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: verify2faApi,
    onSuccess: (user) => {
      qc.setQueryData(sessionQueryOptions.queryKey, user);
    },
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      qc.setQueryData(sessionQueryOptions.queryKey, null);
    },
  });
}

/* ---------- Error → Thai message ---------- */

export function authErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'INVALID_CREDENTIALS':
      return 'อีเมลหรือรหัสผ่านไม่ถูกต้อง';
    case 'ACCOUNT_LOCKED':
      return 'บัญชีถูกล็อกชั่วคราว ลองอีกครั้งใน 15 นาที';
    case 'ACCOUNT_INACTIVE':
      return 'บัญชียังไม่พร้อมใช้งาน กรุณาติดต่อผู้ดูแลระบบ';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลที่กรอกไม่ถูกต้อง';
    case 'CONFIG_ERROR':
      return 'เซิร์ฟเวอร์ตั้งค่าไม่ถูกต้อง กรุณาติดต่อผู้ดูแลระบบ';
    case 'CHALLENGE_INVALID':
      return 'รหัสยืนยันหมดอายุ — กรุณาเข้าสู่ระบบใหม่';
    case 'INVALID_CODE':
      return 'รหัสยืนยันไม่ถูกต้อง';
    case 'INVALID_PASSWORD':
      return 'รหัสผ่านไม่ถูกต้อง';
    case 'ALREADY_ENABLED':
      return '2FA ถูกเปิดใช้งานอยู่แล้ว';
    case 'NOT_ENABLED':
      return '2FA ยังไม่เปิดใช้งาน';
    case 'NETWORK_ERROR':
      return 'เครือข่ายไม่พร้อม กรุณาตรวจการเชื่อมต่อแล้วลองใหม่';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
